/**
 * Types for in-person session reports.
 */

/** In-person report lifecycle (`to_complete` → `validated`, plus `overdue`). */
export type ReportStatus = 'to_complete' | 'draft' | 'submitted' | 'validated' | 'overdue';

/** One pedagogical item from the student-feedback catalog. */
interface StudentFeedbackCatalogItem {
  id: string;
  label: string;
}

/** Catalog category grouping {@link StudentFeedbackCatalogItem}s. */
export interface StudentFeedbackCatalogCategory {
  key: string;
  label: string;
  items: StudentFeedbackCatalogItem[];
}

/** Catalog item enriched by the backend on read. */
export interface SessionReportStudentFeedbackWorkedItem {
  id: string;
  label: string;
  categoryKey: string;
  categoryLabel: string;
}

/** Per-student pedagogical follow-up, stored in `student_feedbacks` (jsonb). */
export interface SessionReportStudentFeedback {
  participantUserId: string;
  workedItemIds: string[];
  /** Labels enriched by the backend in the response (not persisted as-is). */
  workedItems?: SessionReportStudentFeedbackWorkedItem[];
  rating: number | null;
  comment: string | null;
}

/** Attendance value stored on a report participant. */
export type SessionReportAttendanceStatus = 'present' | 'absent' | 'excused' | 'pending';

/** Per-participant attendance patch sent with the report. */
export interface SessionReportParticipantAttendanceItem {
  participantId: string;
  attendanceStatus: SessionReportAttendanceStatus;
  absenceJustification?: string | null;
  absenceComment?: string | null;
}

/** Tutor / volunteer evaluation (backend `author_feedbacks`). */
export interface SessionReportAuthorFeedback {
  idAuteur: string;
  rating: number | null;
  comment: string | null;
  feeling?: SessionReportFeeling | null;
}

/** In-person session report (`GET /session-reports`). */
export interface SessionReport {
  id: string;
  companyId: string;
  sessionId: string;
  authorId: string;
  authorFeedbacks: SessionReportAuthorFeedback[];
  /** @deprecated Prefer `authorFeedbacks` — kept if the API still denormalizes it. */
  rating?: number | null;
  comment?: string | null;
  feeling?: SessionReportFeeling | null;
  /**
   * Retrocompat (text, or an older encoding). Denormalized server-side when `subjectIds` is set.
   */
  subject: string | null;
  /** Subject ids (API / `subject_ids` column). */
  subjectIds?: string[];
  status: ReportStatus;
  validatedById: string | null;
  validatedAt: Date | null;
  filledAt?: Date | null;
  version: number;
  createdAt: Date;
  updatedAt: Date;
  authorName?: string | null;
  sessionDisplayName?: string | null;
  validatedByName?: string | null;
  _permissions?: Record<string, boolean>;
  author?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    displayName: string | null;
  };
  session?: {
    id: string;
    sessionDate: Date;
    type: string;
    establishment?: { id: string; name: string };
  };
  validatedBy?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
  /** Per-student follow-up. Enriched with `workedItems` by the backend. */
  studentFeedbacks?: SessionReportStudentFeedback[];
}

/** Body for `POST /session-reports`. */
export interface CreateReportDto {
  authorId: string;
  authorFeedbacks?: SessionReportAuthorFeedback[];
  /** @deprecated Use `authorFeedbacks`. */
  rating?: number | null;
  comment?: string | null;
  feeling?: SessionReportFeeling | null;
  subject?: string | null;
  subjectIds?: string[];
  status?: ReportStatus;
  authorName?: string | null;
  sessionDisplayName?: string | null;
  participants?: SessionReportParticipantAttendanceItem[];
}

/** Patch of one student follow-up sent in the report PATCH. */
export interface UpdateStudentFeedbackItem {
  participantUserId: string;
  workedItemIds?: string[];
  rating?: number | null;
  comment?: string | null;
}

/** Body for `PATCH /session-reports/:id`. */
export interface UpdateReportDto {
  authorFeedbacks?: SessionReportAuthorFeedback[];
  rating?: number | null;
  comment?: string | null;
  feeling?: SessionReportFeeling | null;
  subject?: string | null;
  subjectIds?: string[];
  status?: ReportStatus;
  validatedById?: string | null;
  authorName?: string | null;
  sessionDisplayName?: string | null;
  validatedByName?: string | null;
  participants?: SessionReportParticipantAttendanceItem[];
  /** Partial patch of per-student pedagogical follow-ups. */
  studentFeedbacks?: UpdateStudentFeedbackItem[];
}

/** List filters for in-person session reports. */
export interface ReportFilters {
  statuses: string[];
  ratings: number[];
  dateRange: 'all' | 'week' | 'month' | 'year';
  customDateFrom?: Date;
  customDateTo?: Date;
  sessionTypes?: string[];
  establishmentIds?: string[];
  search?: string;
}

/** Tutor rating / comment / feeling on create. */
export type TutorFeedbackValue = {
  rating: number | null;
  comment: string;
  feeling: SessionReportFeeling | null;
};

/** Local create-report form: feedback plus subject ids. */
export type CreateReportFormState = TutorFeedbackValue & {
  subjectIds: string[];
};

/** Local edit shape for one student follow-up on a report. */
export type StudentFeedbackEdit = {
  workedItemIds: string[];
  rating: number | null;
  comment: string;
};

/** Query params for `GET /sessions/reports`. */
export interface GetAllReportsParams {
  page?: number;
  pageSize?: number;
  statuses?: string[];
  ratings?: number[];
  dateFrom?: Date;
  dateTo?: Date;
  sessionTypes?: string[];
  establishmentIds?: string[];
  tutorId?: string;
  participantId?: string;
  sessionId?: string;
  sessionGroupId?: string;
  search?: string;
}

/** Canonical feeling values sent to `POST /comptes-rendus`. */
export const SESSION_REPORT_FEELING_VALUES = [
  'very_at_ease',
  'rather_at_ease',
  'neutral',
  'somewhat_difficult',
  'difficult',
] as const;

/** One value from {@link SESSION_REPORT_FEELING_VALUES}. */
export type SessionReportFeeling = (typeof SESSION_REPORT_FEELING_VALUES)[number];
